/**
 * NetIncomeCalculator (corrected for 2025)
 * Fixes: Care insurance surcharge not shared; correct splitting for class 3.
 * CORRECTED: Correct calculation for tax class 2 (relief amount)
 * CORRECTED (NEW): Tax class 2 forces relief amount (regardless of childrenCount)
 * CORRECTED (NEW): Tax class 2 forces 'not childless' for care insurance surcharge
 */

import {
    FederalState,
    HealthInsuranceType,
    NetIncomeResult,
    NetIncomeCalculatorConfig,
    NetIncomeCalculatorParams,
    SocialSecurityResult,
} from '@fxxl/types/net_income_calculator_types';
import { NET_INCOME_CALCULATOR_CONFIG_2025 } from '@fxxl/configs/net_income_calculator_configs';

export default class NetIncomeCalculator {
    public static readonly DEFAULT_CONFIG: NetIncomeCalculatorConfig = NET_INCOME_CALCULATOR_CONFIG_2025;

    private readonly config: NetIncomeCalculatorConfig;

    private readonly grossIncome: number;
    private readonly federalState: FederalState;
    private readonly taxClass: 1 | 2 | 3 | 4 | 5 | 6;
    private readonly isChurchTaxActive: boolean;
    private readonly childrenCount: number;
    private readonly healthInsuranceType: HealthInsuranceType;
    private readonly healthInsuranceAdditionalContributionRate: number;
    private readonly birthYear: number;
    private readonly childless: boolean;

    constructor(
        params: NetIncomeCalculatorParams = {},
        config: NetIncomeCalculatorConfig = NetIncomeCalculator.DEFAULT_CONFIG
    ) {
        this.config = config;

        this.grossIncome = this.toNumber(params.grossIncome, 0);
        this.federalState = this.normalizeFederalState(params.federalState);
        this.taxClass = this.normalizeTaxClass(params.taxClass);
        this.isChurchTaxActive = params.churchTax === true;
        this.childrenCount = Math.max(0, this.toNumber(params.childrenCount, 0));
        this.healthInsuranceType = this.normalizeHealthInsurance(params.healthInsuranceType);
        this.healthInsuranceAdditionalContributionRate = Math.max(0, this.toNumber(params.healthInsuranceAdditionalContribution, 0));
        this.birthYear = this.toNumber(params.birthYear, 1990);
        this.childless = params.childless !== false;
    }

    public calculateNetIncome(): NetIncomeResult {
        const socialSecurity = this.calculateSocialSecurity();
        const monthlyWageTax = this.calculateWageTax();
        const monthlyChurchTax = this.calculateChurchTax();
        const monthlySolidaritySurcharge = this.calculateSolidaritySurcharge();

        const totalDeductionsBeforeRounding =
            socialSecurity.total +
            monthlyWageTax +
            monthlyChurchTax +
            monthlySolidaritySurcharge;

        const totalDeductions = this.round2(totalDeductionsBeforeRounding);
        const monthlyNetIncome = this.round2(this.grossIncome - totalDeductions);

        let deductionRatePercent = 0;
        if (this.grossIncome > 0) {
            deductionRatePercent = (totalDeductions / this.grossIncome) * 100;
        }

        return {
            grossIncome: this.round2(this.grossIncome),
            socialSecurity,
            wageTax: monthlyWageTax,
            churchTax: monthlyChurchTax,
            solidaritySurcharge: monthlySolidaritySurcharge,
            totalDeductions,
            netIncome: monthlyNetIncome,
            deductionRate: Math.round(deductionRatePercent * 10) / 10,
        };
    }

    public calculateSocialSecurity(): SocialSecurityResult {
        const limits = this.config.contributionAssessmentLimits;
        const rates = this.config.contributionRates;

        const assessmentBasisPension = Math.min(this.grossIncome, limits.pensionInsurance);
        const assessmentBasisHealthCare = Math.min(this.grossIncome, limits.healthAndCareInsurance);

        const pensionContributionRate = rates.pensionInsurance / 100;
        const unemploymentContributionRate = rates.unemploymentInsurance / 100;
        const totalHealthContributionRate = (rates.healthInsurance + this.healthInsuranceAdditionalContributionRate) / 100;

        const pensionInsurance = this.round2((assessmentBasisPension * pensionContributionRate) / 2);
        const unemploymentInsurance = this.round2((assessmentBasisPension * unemploymentContributionRate) / 2);

        let healthInsurance = 0;
        let careInsurance = 0;

        if (this.healthInsuranceType === HealthInsuranceType.STATUTORY) {
            healthInsurance = this.round2((assessmentBasisHealthCare * totalHealthContributionRate) / 2);

            // Base is shared, surcharge/relief is employee-only
            let employeeCareRatePercent = rates.careInsurance / 2; // Base employee share (1.8%)

            // CORRECTION 2: Tax class 2 cannot be 'childless'.
            // We only check the 'childless' input if tax class 2 was NOT selected.
            const isChildlessForCareInsurance = this.childless && this.taxClass !== 2;

            if (this.getAgeInYear() >= 23 && isChildlessForCareInsurance) {
                employeeCareRatePercent += rates.careInsuranceSurcharge; // Fully employee (0.6%)
            }

            if (this.childrenCount >= 2) {
                const relief = 0.25 * Math.min(this.childrenCount - 1, 4); // Max 1.0% for 5+ children
                employeeCareRatePercent -= relief;
            }

            if (employeeCareRatePercent < 0) {
                employeeCareRatePercent = 0;
            }

            const employeeCareContributionRate = employeeCareRatePercent / 100;
            careInsurance = this.round2(assessmentBasisHealthCare * employeeCareContributionRate);
        }

        const total = this.round2(
            pensionInsurance +
            unemploymentInsurance +
            healthInsurance +
            careInsurance
        );

        return {
            pensionInsurance,
            unemploymentInsurance,
            healthInsurance,
            careInsurance,
            total,
        };
    }

    public calculateWageTax(): number {
        const socialSecurity = this.calculateSocialSecurity();

        const annualGrossIncome = this.grossIncome * 12;
        const annualSocialSecurity = socialSecurity.total * 12;
        const grundfreibetrag = this.config.taxAllowances.basicAllowanceYear; // 12.096 € (2025)

        // Standardwerte für Pauschbeträge laden (Werbungskosten & Sonderausgaben)
        let annualIncomeRelatedExpenses = this.config.taxAllowances.incomeRelatedExpensesLumpSumYear; // 1.230 €
        let annualSpecialExpenses = this.config.taxAllowances.specialExpensesLumpSumYear; // 36 €

        // Kinderfreibetrag
        let annualChildAllowance = this.config.taxAllowances.childAllowanceYear * this.childrenCount;

        // KORREKTUR: Unterschied zwischen Klasse 5 und 6 bei den Pauschbeträgen
        if (this.taxClass === 6) {
            // Steuerklasse 6: KEINE Pauschbeträge, da diese bereits im ersten Dienstverhältnis abgegolten sind.
            annualIncomeRelatedExpenses = 0;
            annualSpecialExpenses = 0;
            annualChildAllowance = 0;
        } else if (this.taxClass === 5) {
            // Steuerklasse 5: Erhält Pauschbeträge, aber normalerweise keinen Kinderfreibetrag (liegt bei Kl. 3).
            annualChildAllowance = 0;
        }

        // Entlastungsbetrag für Alleinerziehende (nur Steuerklasse 2)
        let reliefAmount = 0;
        if (this.taxClass === 2) {
            const baseAmount = 5052;
            const increasePerChild = 240;
            reliefAmount = baseAmount;
            if (this.childrenCount > 1) {
                reliefAmount += (this.childrenCount - 1) * increasePerChild;
            }
        }

        // Zu versteuerndes Einkommen berechnen
        // HINWEIS: Für Klasse 6 sind die Expenses 0, daher ist das zu versteuernde Einkommen hier HÖHER als bei Klasse 5.
        let annualTaxableIncome = annualGrossIncome - annualSocialSecurity - annualIncomeRelatedExpenses - annualSpecialExpenses - annualChildAllowance - reliefAmount;

        if (annualTaxableIncome < 0) {
            annualTaxableIncome = 0;
        }

        let annualTax = 0;

        if (this.taxClass === 3) {
            // Splitting-Verfahren
            const halvedTaxableIncome = annualTaxableIncome / 2;
            annualTax = this.config.incomeTaxTariff(halvedTaxableIncome) * 2;
        } else {
            // Grundtarif (Klasse 1, 2, 4, 5, 6)
            let einkommenFuerTarif = annualTaxableIncome;

            // KORREKTUR FÜR 5 & 6 (Grundfreibetrag-Logik):
            // Beide Klassen haben keinen Anspruch auf den Grundfreibetrag (die ersten 12.096 € steuerfrei).
            // Da unsere Tarif-Formel diesen Freibetrag aber automatisch abzieht, addieren wir ihn hier wieder drauf.
            // Damit beginnt die Versteuerung effektiv ab dem ersten Euro.
            if (this.taxClass === 5 || this.taxClass === 6) {
                einkommenFuerTarif += grundfreibetrag;
            }

            annualTax = this.config.incomeTaxTariff(einkommenFuerTarif);
        }

        let monthlyWageTax = annualTax / 12;

        if (monthlyWageTax < 0) {
            monthlyWageTax = 0;
        }

        return this.round2(monthlyWageTax);
    }

    public calculateChurchTax(): number {
        let churchTaxValue = 0;

        if (this.isChurchTaxActive) {
            const monthlyWageTax = this.calculateWageTax();

            const churchTaxRate =
                this.config.churchTaxRates[this.federalState] ?? 9;

            const churchTaxFactor = churchTaxRate / 100;
            const intermediateValue = monthlyWageTax * churchTaxFactor;

            churchTaxValue = this.round2(intermediateValue);
        }

        return churchTaxValue;
    }

    public calculateSolidaritySurcharge(): number {
        const monthlyWageTax = this.calculateWageTax();
        const annualWageTax = monthlyWageTax * 12;

        const threshold = this.taxClass === 3
            ? this.config.solidaritySurchargeThresholds.class3
            : this.config.solidaritySurchargeThresholds.class1_2_4_5_6;

        let monthlySurcharge = 0;

        if (annualWageTax > threshold) {
            const annualSurcharge = annualWageTax * 0.055;
            const surchargePerMonth = annualSurcharge / 12;

            monthlySurcharge = this.round2(surchargePerMonth);
        }

        return monthlySurcharge;
    }

    // Private helper methods
    private round2(value: number): number {
        const rounded = Math.round((value + Number.EPSILON) * 100) / 100;
        return rounded;
    }

    private getAgeInYear(): number {
        const age = this.config.year - this.birthYear;
        return age;
    }

    private toNumber(value: unknown, fallback: number): number {
        let result = fallback;

        if (typeof value === 'string') {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) {
                result = parsed;
            }
        } else if (typeof value === 'number' && Number.isFinite(value)) {
            result = value;
        }

        return result;
    }

    private normalizeFederalState(input?: NetIncomeCalculatorParams['federalState']): FederalState {
        let result = FederalState.HE;

        const raw = (input ?? FederalState.HE).toString().toUpperCase();
        if (raw in FederalState) {
            result = raw as FederalState;
        }

        return result;
    }

    private normalizeTaxClass(input?: NetIncomeCalculatorParams['taxClass']): 1 | 2 | 3 | 4 | 5 | 6 {
        const fallback: 1 | 2 | 3 | 4 | 5 | 6 = 1;
        const parsed = this.toNumber(input, fallback);
        const validTaxClasses: Array<1 | 2 | 3 | 4 | 5 | 6> = [1, 2, 3, 4, 5, 6];

        let result: 1 | 2 | 3 | 4 | 5 | 6 = fallback;
        if (validTaxClasses.includes(parsed as 1 | 2 | 3 | 4 | 5 | 6)) {
            result = parsed as 1 | 2 | 3 | 4 | 5 | 6;
        }

        return result;
    }

    private normalizeHealthInsurance(input?: NetIncomeCalculatorParams['healthInsuranceType']): HealthInsuranceType {
        let result = HealthInsuranceType.STATUTORY;

        const raw = (input ?? HealthInsuranceType.STATUTORY).toString().toLowerCase();
        if (raw === HealthInsuranceType.PRIVATE) {
            result = HealthInsuranceType.PRIVATE;
        }

        return result;
    }
}